import { useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ExpertDoc } from 'packages/types'
import {
  ResumeDetails,
  selectors as resumeDistributionSelectors,
  actions as resumeDistributionActions,
} from 'builder/modules/resumeDistribution'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions as resumeReviewActions } from 'builder/modules/resumeReview'
import {
  selectors as panelSelectors,
  actions as panelActions,
  BriefResume,
} from 'builder/modules/panel'
import { actions as renderingActions } from 'builder/modules/rendering'
import {
  DocumentTypes as TYPES,
  FormatType,
  FetchStatuses,
  DocumentTypes,
} from 'builder/modules/constants'
import { Questionnaire, ResumePreviewModal } from 'builder/components/ResumeDistributionV2'
import { ElementHighlight } from 'builder/components/DocumentEditor/ElementHighlight'
import { useI18n } from 'builder/hooks/useI18n'
import { actions } from 'builder/modules/resumeEditor'
import { DATA_HIGHLIGHT_ID } from '../../components/PersonalDetailsForm/ResumePreview/ResumeBuilderPreview'

const RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN =
  'RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN'

const ResumeDistributionEditView = () => {
  const dispatch = useDispatch()
  const [query] = useSearchParams()
  const resumeId = query.get('resume_id')
  const documentType = query.get('document_type')
  const documentFormat = query.get('document_format')
  const documentTemplate = query.get('document_template')
  const location = useLocation()
  const navigate = useNavigate()
  const selectedResumeDetails = useTypedSelector<ResumeDetails>(
    resumeDistributionSelectors.selectedResumeDetails,
  )
  const isResumePreviewOpen = useTypedSelector(resumeDistributionSelectors.isResumePreviewOpen)
  const { i18n } = useI18n()
  const documentsList = useTypedSelector(panelSelectors.documents)
  const documentsStatus = useTypedSelector(panelSelectors.documentsStatus)
  const resumeList = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.resume),
  )
  const trDocuments = useTypedSelector(store =>
    panelSelectors.documentsByType(store, DocumentTypes.trDocument),
  )
  const lastDocumentRef = useRef<BriefResume | ExpertDoc | null>(null)
  const completeResume = useTypedSelector(resumeDistributionSelectors.formResumeFetchDetails)
  const isParsing = useTypedSelector<boolean>(resumeDistributionSelectors.isParsing)

  const candidate = useTypedSelector(resumeDistributionSelectors.candidate)
  const resumeDetails = useTypedSelector(resumeDistributionSelectors.selectedResumeDetails)

  const trResumes = useMemo(
    () =>
      Array.isArray(trDocuments)
        ? trDocuments.filter(item => item.doc_type === DocumentTypes.resume)
        : [],
    [trDocuments],
  )

  useEffect(() => {
    dispatch(panelActions.fetchDocuments())
  }, [dispatch])

  useEffect(() => {
    if (
      documentsList?.length === 0 &&
      documentsStatus === FetchStatuses.loaded &&
      !completeResume &&
      !isParsing &&
      !selectedResumeDetails
    ) {
      navigate('/resume-distribution')
    }
  }, [completeResume, documentsList, documentsStatus, isParsing, navigate, selectedResumeDetails])

  useEffect(() => {
    const id = candidate?.resumeId
    if (!id) return
    if (resumeDetails) return
    if (lastDocumentRef.current) return

    const resume = Array.isArray(resumeList) ? resumeList.find(item => item.id === id) : undefined
    const trResume = Array.isArray(trResumes) ? trResumes.find(item => item.id === id) : undefined
    const document = resume ?? trResume

    if (document) {
      dispatch(resumeDistributionActions.setResumeDetails(document))
      lastDocumentRef.current = document
    }
  }, [candidate?.resumeId, resumeDetails, resumeList, trResumes, dispatch])

  useEffect(() => {
    if (resumeId && documentType) {
      dispatch(
        renderingActions.download({
          id: Number(resumeId),
          type: documentType as TYPES,
          format: documentFormat as FormatType,
          template: documentTemplate as string,
        }),
      )

      if (documentType !== TYPES.coverLetter)
        dispatch(resumeReviewActions.sendResume({ resumeId: Number(resumeId) }))
      navigate(`${location.pathname}?resume_id=${resumeId}`, { replace: true })
    }
  }, [documentType, documentFormat, documentTemplate, resumeId, location, dispatch])

  useEffect(() => {
    const isHighlightShown = localStorage.getItem(RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN)
    if (!isHighlightShown) {
      dispatch(
        actions.setHighlightedElement({
          title: i18n.t(
            'builder.resume_distribution.version_3.questionnaire.resume_preview_box.highlighted_element.title',
          ),
          content: i18n.t(
            'builder.resume_distribution.version_3.questionnaire.resume_preview_box.highlighted_element.description',
          ),
          target: `[data-highlight-id=${DATA_HIGHLIGHT_ID}]`,
          onClose: () => {
            localStorage.setItem(RESUME_DISTRIBUTION_CHANGE_RESUME_HIGHLIGHT_SHOWN, 'true')
          },
        }),
      )
    }
  }, [dispatch, i18n])

  return (
    <>
      <Questionnaire />
      {isResumePreviewOpen && <ResumePreviewModal />}
      <ElementHighlight />
    </>
  )
}

export default ResumeDistributionEditView
